"use client";
import {
  Alert,
  Button,
  FormError,
  Input,
  Label,
  NextLink,
  notify,
} from "cy-fe";
import React, { ReactNode, useState } from "react";
import { HeaderText } from "../components/HeaderText";
import * as StepWizard from "cy-ui/components/StepWizard";
import { BodyContainer } from "cy-remittance/components/layouts";
import {
  PhoneNumberInput,
  PhoneNumber,
  validatePhoneNumber,
} from "cy-ui/components/PhoneNumber";
import { LoginRequest } from "cy-remittance/SignIn";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { getCustomerSignUpStatusClient } from "cy-api/simple-payments/customer/getCustomerSignUpStatusClient";
import { ErrorBanner } from "cy-remittance/components/ErrorBanner";

export function MobileNumberPage({
  header,
  onContinue,
  initialMobile,
  signinUrl,
  postLoginUrl,
  allowedCountryCodes,
}: {
  onContinue: (mobile: PhoneNumber) => void;
  header: ReactNode;
  initialMobile?: PhoneNumber;
  signinUrl: string;
  postLoginUrl: string;
  allowedCountryCodes?: string[];
}) {
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [customerStatus, setCustomerStatus] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>();
  const [submitted, setSubmitted] = useState(false);
  const [mobile, setMobile] = useState<PhoneNumber>(
    initialMobile || { isd: "", number: "" },
  );

  const activeCustomer = customerStatus === "active";
  const incompleteCustomer = customerStatus === "pending";
  const mobileError = validatePhoneNumber(mobile);

  const checkLoginHandler = async (args: PhoneNumber) => {
    if (!args) {
      return;
    }

    setLoading(true);

    const [resp, err] = await getCustomerSignUpStatusClient({
      mobile: args,
    });

    setLoading(false);

    if (err) {
      notify("Error", "Error in request", { error: err });
    } else {
      if (resp.status === "new") {
        onContinue(args);
      }
      setCustomerStatus(resp.status);
    }
  };

  async function loginHandler(args: PhoneNumber) {
    setError("");

    if (!args) {
      return;
    }

    const req: LoginRequest = {
      password,
      mobile_isd: args.isd,
      mobile_number: args.number,
    };
    const res = await signIn("credentials", {
      redirect: false,
      ...req,
    });

    if (!res?.ok) {
      setError("Your login credentials were invalid");
    } else {
      // bounce to homepage to determine what to do next
      router.push(postLoginUrl);
    }
  }

  const handlePhoneChange = (value: PhoneNumber) => {
    setMobile(value);
    setCustomerStatus("");
    setError("");
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    try {
      setLoading(true);
      setSubmitted(true);
      e.preventDefault();
      if (!mobileError) {
        if (activeCustomer || incompleteCustomer) {
          await loginHandler(mobile);
        } else {
          await checkLoginHandler(mobile);
        }
      }
    } finally {
      setLoading(false);
    }
  }
  return (
    <StepWizard.StepPageLayout
      headerRender={() => <HeaderText vCenter>{header}</HeaderText>}
      subHeaderRender={() => (
        <>
          Already have an account?{" "}
          <NextLink
            className="font-semibold text-black underline underline-offset-2"
            href={signinUrl}
          >
            {" "}
            Log in
          </NextLink>
        </>
      )}
    >
      <BodyContainer className="mt-10">
        <h2>First, provide some login details:</h2>

        {error ? <ErrorBanner error={error} /> : null}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-sm font-light">Your mobile number</Label>
            <PhoneNumberInput
              value={{
                isd: mobile.isd || "",
                number: mobile.number || "",
              }}
              error={submitted && !!mobileError}
              onChange={handlePhoneChange}
              allowedCountryCodes={allowedCountryCodes}
            />
            <FormError>
              {submitted && !!mobileError
                ? `Invalid mobile (${mobileError})`
                : null}
            </FormError>
          </div>

          {(!!activeCustomer || !!incompleteCustomer) && (
            <>
              <Alert vType="info">
                {incompleteCustomer
                  ? "Looks like you were in the process of signing up. Please enter your password to continue"
                  : "Looks like you already have an account with us. Please enter your password to login"}
              </Alert>
              <div>
                <Label htmlFor="input-password-for-credentials-provider">
                  Your password
                </Label>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  vSize="lg"
                  id="input-password-for-credentials-provider"
                  name="password"
                  placeholder=""
                  required
                  type="password"
                />
              </div>
              <NextLink
                className="mt-4 ml-2 block font-semibold underline"
                href="/public/reset-password"
              >
                Forgot password?
              </NextLink>
            </>
          )}

          <Button
            type="submit"
            className="!mt-10 w-full"
            loading={loading}
            vSize="lg"
          >
            {activeCustomer ? "Log in" : "Next"}
          </Button>
        </form>
      </BodyContainer>
    </StepWizard.StepPageLayout>
  );
}
